import { useAvailableAuction } from 'api/auctions'
import classNames from 'classnames'
import { Button } from 'components/common/Button'
import { useFeatureFlags } from 'hooks'
import { useCountdown } from 'hooks/useCountdown'
import useTranslation from 'next-translate/useTranslation'
import Image from 'next/image'
import { PropsWithClassName } from 'types'
import { getImageUrl } from 'utils/image'

export function AvailableAuctionSection({ className }: PropsWithClassName) {
  const { t } = useTranslation('common')
  const { availableAuction } = useAvailableAuction()
  const { featureFlags } = useFeatureFlags()

  const targetDate = availableAuction?.status?.pending
    ? availableAuction?.status.startDate
    : availableAuction?.status.endDate
  const countdown = useCountdown(targetDate?.toDate())

  if (!availableAuction || !featureFlags?.['enable-auction']) return null

  const image = availableAuction.artworkImages?.data?.[0]

  return (
    <div className={classNames('2xl:container md:px-12', className)}>
      <section
        className={classNames(
          'flex w-full flex-col items-center justify-center gap-y-5 bg-warm-100 p-6 md:gap-y-4 md:rounded-lg md:px-12 lg:grid lg:grid-cols-3 lg:gap-x-4'
        )}
      >
        <div className='flex flex-col items-center justify-center gap-y-3 md:flex-row md:gap-x-10'>
          <div className='text-center'>
            <p className='font-bold md:text-xl'>
              {availableAuction.artistName}
            </p>
            <p className='md:text-lg'>{t('original_artwork_auction')}</p>
          </div>

          {image && (
            <Image
              src={getImageUrl(image, 'medium')}
              alt={
                availableAuction.artworkName || t('original_artwork_auction')
              }
              width={125}
              height={170}
              className='object-contain object-center lg:hidden'
              quality={90}
            />
          )}
        </div>

        <div className='w-full space-y-2.5 rounded-lg bg-cool-50 px-6 py-4 md:py-6'>
          <p className='text-center text-sm font-bold'>
            {availableAuction.status.pending ? t('starts_in') : t('ends_in')}:
          </p>

          <div className='grid grid-cols-4 items-center justify-center gap-x-2.5'>
            <Unit
              label={t(
                String(countdown.days) === '1'
                  ? 'countdown.days.one'
                  : 'countdown.days.other'
              )}
              value={countdown.days}
            />
            <Unit label={t('countdown.hrs')} value={countdown.hrs} />
            <Unit label={t('countdown.mins')} value={countdown.min} />
            <Unit label={t('countdown.sec')} value={countdown.sec} />
          </div>

          <Button
            style='solid-primary'
            pill
            size='md'
            type='link'
            href={availableAuction.url}
          >
            {t('view')}
          </Button>
        </div>

        {image && (
          <Image
            src={getImageUrl(image, 'medium')}
            alt={availableAuction.artworkName || t('original_artwork_auction')}
            width={125}
            height={170}
            className='mx-auto hidden object-contain object-center lg:block'
            quality={90}
          />
        )}
      </section>
    </div>
  )
}

function Unit({ label, value }: { label: string; value: string }) {
  return (
    <div className='flex w-full flex-col items-center justify-center rounded-lg bg-primary-light/70 p-2.5'>
      <p className='font-bold text-purple-600'>{value}</p>
      <p className='text-xs font-medium'>{label}</p>
    </div>
  )
}
